/**
 * Should I Spray Today?
 * =====================
 *
 * One screen, one answer. Spare the user from opening four tabs to decide
 * whether to go outside with a backpack sprayer.
 *
 * Calls /api/spray-today (requires auth + a set location) and renders:
 *   - Verdict banner (spray / caution / hold) with the single headline
 *   - Four reason tiles (Weather / Disease / Resistance / Moisture)
 *   - Recommended actives list (only if disease pressure + spray signal)
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../config/api';

const VERDICT_META = {
  spray: {
    label: 'Spray today',
    tone: 'bg-emerald-600 border-emerald-700 text-white',
    dot: 'bg-emerald-400',
  },
  caution: {
    label: 'Marginal',
    tone: 'bg-amber-500 border-amber-600 text-white',
    dot: 'bg-amber-200',
  },
  hold: {
    label: 'Hold off',
    tone: 'bg-rose-600 border-rose-700 text-white',
    dot: 'bg-rose-300',
  },
};

function Tile({ label, value, sub, muted = false, testid }) {
  return (
    <div
      className={`rounded-xl border p-4 ${muted ? 'bg-slate-50 border-slate-200' : 'bg-white border-slate-200'}`}
      data-testid={testid}
    >
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`text-xl font-semibold mt-1 ${muted ? 'text-slate-400' : 'text-slate-900'}`}>{value}</div>
      {sub ? <div className="text-xs text-slate-500 mt-1">{sub}</div> : null}
    </div>
  );
}

export default function SprayTodayPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await axios.get(`${API_URL}/api/spray-today/`, { withCredentials: true });
      setData(r.data);
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Could not reach spray recommender.';
      setErr(detail);
      if (e?.response?.status === 401) {
        setErr('Please sign in to see your local spray recommendation.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const verdict = data?.verdict ? VERDICT_META[data.verdict] : null;

  return (
    <div className="min-h-screen bg-slate-100 px-5 py-8" data-testid="spray-today-page">
      <div className="max-w-3xl mx-auto">
        <button
          onClick={() => navigate(-1)}
          className="text-xs uppercase tracking-widest text-slate-600 hover:text-slate-900 mb-3"
          data-testid="spray-today-back-btn"
        >
          ← Back
        </button>
        <div className="text-[11px] uppercase tracking-widest text-slate-500">TurfPulse · Decision</div>
        <h1 className="text-3xl font-semibold text-slate-900 mt-1">Should I spray today?</h1>
        <p className="text-slate-600 text-sm mt-1 max-w-xl">
          One number, one answer. We fuse your local forecast, dollar-spot +
          large-patch pressure, and your recent product rotation into a single call.
        </p>

        {err ? (
          <div className="mt-5 bg-rose-50 border border-rose-200 rounded-xl px-4 py-3 text-sm text-rose-800" data-testid="spray-today-error">
            {err}
            {err.includes('location') ? (
              <button
                className="ml-2 underline font-semibold"
                onClick={() => navigate('/profile')}
                data-testid="spray-today-set-location-link"
              >
                Set location
              </button>
            ) : null}
          </div>
        ) : null}

        {loading && !data ? (
          <div className="mt-6 h-32 rounded-2xl bg-white border border-slate-200 animate-pulse" />
        ) : null}

        {verdict ? (
          <div
            className={`mt-5 rounded-2xl border-2 px-6 py-6 ${verdict.tone}`}
            data-testid={`spray-today-verdict-${data.verdict}`}
          >
            <div className="flex items-center gap-3 mb-2">
              <span className={`w-2.5 h-2.5 rounded-full ${verdict.dot} animate-pulse`} />
              <span className="text-[10px] uppercase tracking-widest opacity-80">Verdict</span>
            </div>
            <div className="text-4xl font-bold tracking-tight" data-testid="spray-today-verdict-label">
              {verdict.label}
            </div>
            <div className="text-base mt-2 opacity-95 leading-relaxed" data-testid="spray-today-headline">
              {data.headline}
            </div>
            {data.next_check ? (
              <div className="text-xs uppercase tracking-widest opacity-80 mt-4">
                Next check · {data.next_check}
              </div>
            ) : null}
          </div>
        ) : null}

        {data?.windows ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
            <Tile
              label="Weather"
              value={data.windows.weather?.label || '—'}
              sub={
                (data.windows.weather?.reasons || []).slice(0, 1).join(', ') || 'no data'
              }
              testid="spray-today-tile-weather"
            />
            <Tile
              label="Dollar Spot"
              value={
                (data.windows.disease?.sk_band || 'unknown').replace(/^\w/, (c) => c.toUpperCase())
              }
              sub={
                data.windows.disease?.sk_prob != null
                  ? `${data.windows.disease.sk_prob}% probability`
                  : '—'
              }
              testid="spray-today-tile-dollar-spot"
            />
            <Tile
              label="Large Patch"
              value={(data.windows.disease?.lp_band || 'unknown').replace(/^\w/, (c) => c.toUpperCase())}
              sub="warm-season turf"
              testid="spray-today-tile-large-patch"
            />
            <Tile
              label="Soil Moisture"
              value="—"
              sub={data.windows.moisture?.reason || 'no sensor'}
              muted
              testid="spray-today-tile-moisture"
            />
          </div>
        ) : null}

        {data?.recommended_actives?.length ? (
          <div className="mt-5 bg-white border border-slate-200 rounded-xl p-5" data-testid="spray-today-actives">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">
              Rotate-safe actives for this condition
            </div>
            <ul className="space-y-2">
              {data.recommended_actives.map((a) => (
                <li key={`${a.name}-${a.frac_group}`} className="flex items-baseline justify-between gap-3">
                  <div>
                    <div className="text-slate-900 font-semibold">{a.name}</div>
                    <div className="text-xs text-slate-500">{a.why}</div>
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-0.5">
                    FRAC {a.frac_group}
                  </div>
                </li>
              ))}
            </ul>
            <div className="text-[11px] text-slate-500 mt-3 italic">
              Suggestions based on your <button onClick={() => navigate('/resistance')} className="underline">resistance log</button> over the last 30 days.
            </div>
          </div>
        ) : null}

        {data?.windows?.resistance?.recent_frac_groups_30d?.length ? (
          <div className="mt-4 text-xs text-slate-500" data-testid="spray-today-recent-frac">
            Recently used FRAC groups: {data.windows.resistance.recent_frac_groups_30d.join(', ')}
          </div>
        ) : null}
      </div>
    </div>
  );
}
